import { useReducer, useEffect, useState, useCallback } from "react";
import useU2000LocalStorage from "../hooks/U2000UseLocalStorage";
import { useU2000AuthContext } from "../context/auth/U2000UseAuthContext";
import type { U2000FavoriteMenuItem, U2000FavoriteMenuState, U2000FavoriteMenuFilter, U2000FavoriteMenuSettings } from "../types/U2000FavoriteMenu";
import {
  u2000CanSetDefaultScreenPath,
  u2000ParseFavoriteItemsFromStorage,
  u2000ResolveDefaultScreenDecision,
  u2000ResolveFavoriteItemsList,
} from "../tools/u2000FavoriteDefaultScreenUtils";
import { v4 as uuidv4 } from "uuid";

// Actions pour le reducer
export const U2000FavoriteMenuActionType = {
  SET_ITEMS: "SET_ITEMS",
  ADD_ITEM: "ADD_ITEM",
  UPDATE_ITEM: "UPDATE_ITEM",
  REMOVE_ITEM: "REMOVE_ITEM",
  REORDER_ITEMS: "REORDER_ITEMS",
  SET_GROUPS: "SET_GROUPS",
  ADD_GROUP: "ADD_GROUP",
  UPDATE_GROUP: "UPDATE_GROUP",
  REMOVE_GROUP: "REMOVE_GROUP",
  MOVE_ITEM_TO_GROUP: "MOVE_ITEM_TO_GROUP",
  UPDATE_USAGE: "UPDATE_USAGE",
  CLEANUP_OLD_ITEMS: "CLEANUP_OLD_ITEMS",
  RESET_STATE: "RESET_STATE",
} as const;

export type U2000FavoriteMenuActionTypeKeys = keyof typeof U2000FavoriteMenuActionType;

// Action types pour le reducer
interface U2000FavoriteMenuAction {
  type: U2000FavoriteMenuActionTypeKeys;
  payload?: any;
}

// Reducer pour la gestion du state des favoris
const favoriteMenuReducer = (state: U2000FavoriteMenuState, action: U2000FavoriteMenuAction): U2000FavoriteMenuState => {
  switch (action.type) {
    case U2000FavoriteMenuActionType.SET_ITEMS:
      return {
        ...state,
        items: action.payload.items,
      };

    case U2000FavoriteMenuActionType.ADD_ITEM:
      return {
        ...state,
        items: [...state.items, action.payload.item],
      };

    case U2000FavoriteMenuActionType.UPDATE_ITEM:
      return {
        ...state,
        items: state.items.map((item) => (item.id === action.payload.id ? { ...item, ...action.payload.updates } : item)),
      };

    case U2000FavoriteMenuActionType.REMOVE_ITEM:
      return {
        ...state,
        items: state.items.filter((item) => item.id !== action.payload.id),
      };

    case U2000FavoriteMenuActionType.REORDER_ITEMS:
      return {
        ...state,
        items: action.payload.items,
      };

    case U2000FavoriteMenuActionType.UPDATE_USAGE:
      return {
        ...state,
        items: state.items.map((item) =>
          item.id === action.payload.id
            ? {
                ...item,
                lastUsed: new Date().toISOString(),
                useCount: (item.useCount || 0) + 1,
              }
            : item
        ),
      };

    case U2000FavoriteMenuActionType.CLEANUP_OLD_ITEMS:
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - (action.payload.days || 30));
      return {
        ...state,
        items: state.items.filter((item) => {
          if (!item.lastUsed || item.isDefault) return true;
          return new Date(item.lastUsed) > cutoffDate;
        }),
      };

    case U2000FavoriteMenuActionType.RESET_STATE:
      return {
        items: [],
        groups: [],
        lastSyncDate: new Date().toISOString(),
      };

    default:
      return state;
  }
};

// Interface pour les paramètres du hook
export interface U2000FavoriteMenuHandlersParams {
  luName?: string;
  settings?: U2000FavoriteMenuSettings;
}

// Interface pour le retour du hook
export interface U2000FavoriteMenuHandlersReturn {
  // Gestion des items
  getFavoriteItems: () => U2000FavoriteMenuItem[];
  addFavoriteItem: (item: Omit<U2000FavoriteMenuItem, "id" | "createdAt" | "order">) => void;
  removeFavoriteItem: (itemId: string) => void;
  updateFavoriteItem: (itemId: string, updates: Partial<U2000FavoriteMenuItem>) => void;
  toggleFavorite: (path: string, name: string, icon?: string, category?: string) => boolean;
  isFavorite: (path: string) => boolean;

  // Gestion de l'ordre
  moveFavoriteItem: (itemId: string, direction: "up" | "down" | "top" | "bottom") => void;
  reorderFavoriteItems: (items: U2000FavoriteMenuItem[]) => void;

  // Gestion des filtres et recherche
  getFilteredItems: (filter?: U2000FavoriteMenuFilter) => U2000FavoriteMenuItem[];
  getItemsByCategory: () => Record<string, U2000FavoriteMenuItem[]>;
  getMostUsedItems: (limit?: number) => U2000FavoriteMenuItem[];
  getRecentItems: (limit?: number) => U2000FavoriteMenuItem[];

  // Gestion de l'usage
  trackUsage: (path: string) => void;

  // Gestion des settings
  getSettings: () => U2000FavoriteMenuSettings;
  updateSettings: (settings: Partial<U2000FavoriteMenuSettings>) => void;

  // Gestion de l'écran par défaut
  getDefaultScreen: () => string | null;
  setDefaultScreen: (path: string) => void;
  clearDefaultScreen: () => void;

  // Utilitaires
  exportFavorites: () => string;
  importFavorites: (jsonData: string) => boolean;
  cleanupOldItems: (days?: number) => void;
  resetFavorites: () => void;
  reloadData: () => void;

  // État
  isLoading: boolean;
  itemCount: number;
}

// Hook principal pour la gestion des menus favoris
export function useU2000FavoriteMenuHandlers(params: U2000FavoriteMenuHandlersParams = {}): U2000FavoriteMenuHandlersReturn {
  const { luName = "default", settings: initialSettings } = params;
  const { auth } = useU2000AuthContext();

  // Construire une clé localStorage spécifique à l'utilisateur et l'organisation
  const userSpecificKey = auth.isAuthenticated && auth.user.userName ? `${luName}_${auth.user.userName}_${auth.user.organizationId || "noorg"}` : luName;

  // Initialisation du state avec une fonction pour éviter les ré-initialisations
  const [state, dispatch] = useReducer(favoriteMenuReducer, null, () => ({
    items: [],
    groups: [],
    lastSyncDate: new Date().toISOString(),
  }));
  const [localFavorites, setLocalFavorites] = useU2000LocalStorage<U2000FavoriteMenuItem[]>(`${userSpecificKey}FavoriteMenus`, []);

  const [localSettings, setLocalSettings] = useU2000LocalStorage<U2000FavoriteMenuSettings>(`${userSpecificKey}FavoriteMenuSettings`, {
    maxFavorites: 50,
    autoCleanupDays: 30,
    groupByCategory: true,
    showUsageCount: true,
    ...initialSettings,
  });
  const [isLoading, setIsLoading] = useState(true);

  // Surveillance des changements d'utilisateur pour réinitialiser les favoris.
  // Ne pas réinitialiser lors d'un simple changement d'organisation pour le même utilisateur.
  useEffect(() => {
    // Réinitialiser le state quand le nom d'utilisateur change (vrai changement d'utilisateur)
    dispatch({
      type: U2000FavoriteMenuActionType.RESET_STATE,
    });
    setIsLoading(true);
    // Déclencher uniquement lorsque le nom d'utilisateur change
  }, [auth.user.userName]);

  // Synchronisation avec le localStorage
  useEffect(() => {
    // Charger les données du localStorage au démarrage ou après changement de clé utilisateur/organisation
    if (localFavorites && localFavorites.length > 0) {
      dispatch({
        type: U2000FavoriteMenuActionType.SET_ITEMS,
        payload: { items: localFavorites },
      });
    } else {
      // Si la nouvelle organisation n'a pas d'entrées en localStorage, réinitialiser
      dispatch({
        type: U2000FavoriteMenuActionType.RESET_STATE,
      });
    }
    setIsLoading(false);
    // Déclencher aussi lorsque la clé utilisateur/organisation change (userSpecificKey)
  }, [localFavorites, userSpecificKey]);

  // Mise à jour du localStorage quand les items changent
  const updateLocalStorage = useCallback(
    (items: U2000FavoriteMenuItem[]) => {
      setLocalFavorites(items);
    },
    [setLocalFavorites]
  );

  // Lecture directe localStorage (BL-0337 : instances / state stale)
  const readFavoriteItemsFromStorage = useCallback((): U2000FavoriteMenuItem[] => {
    const itemsStorageKey = `${userSpecificKey}FavoriteMenus`;
    return u2000ParseFavoriteItemsFromStorage(localStorage.getItem(itemsStorageKey));
  }, [userSpecificKey]);

  // Fonctions de gestion des items
  const getFavoriteItems = useCallback(() => {
    return u2000ResolveFavoriteItemsList(state.items, localFavorites || [], readFavoriteItemsFromStorage());
  }, [state.items, localFavorites, readFavoriteItemsFromStorage]);

  const addFavoriteItem = useCallback(
    (item: Omit<U2000FavoriteMenuItem, "id" | "createdAt" | "order">) => {
      const currentItems = getFavoriteItems();
      const settings = localSettings;

      // Vérifier la limite
      if (settings.maxFavorites && currentItems.length >= settings.maxFavorites) {
        console.warn(`Maximum number of favorites reached (${settings.maxFavorites})`);
        return;
      }

      // Vérifier si l'item existe déjà
      const existingItem = currentItems.find((favItem) => favItem.path === item.path);
      if (existingItem) {
        console.warn("Item already exists in favorites");
        return;
      }

      const newItem: U2000FavoriteMenuItem = {
        ...item,
        id: uuidv4(),
        createdAt: new Date().toISOString(),
        order: currentItems.length,
        useCount: 0,
      };

      const updatedItems = [...currentItems, newItem];

      // Mettre à jour les deux: le state ET le localStorage
      dispatch({
        type: U2000FavoriteMenuActionType.ADD_ITEM,
        payload: { item: newItem },
      });
      updateLocalStorage(updatedItems);
    },
    [getFavoriteItems, updateLocalStorage, localSettings]
  );

  const removeFavoriteItem = useCallback(
    (itemId: string) => {
      const currentItems = getFavoriteItems();
      const updatedItems = currentItems.filter((item) => item.id !== itemId);

      // Réajuster l'ordre
      const reorderedItems = updatedItems.map((item, index) => ({
        ...item,
        order: index,
      }));

      dispatch({
        type: U2000FavoriteMenuActionType.REMOVE_ITEM,
        payload: { id: itemId },
      });
      updateLocalStorage(reorderedItems);
    },
    [getFavoriteItems, updateLocalStorage]
  );

  const updateFavoriteItem = useCallback(
    (itemId: string, updates: Partial<U2000FavoriteMenuItem>) => {
      const currentItems = getFavoriteItems();
      const updatedItems = currentItems.map((item) => (item.id === itemId ? { ...item, ...updates } : item));

      dispatch({
        type: U2000FavoriteMenuActionType.UPDATE_ITEM,
        payload: { id: itemId, updates },
      });
      updateLocalStorage(updatedItems);
    },
    [getFavoriteItems, updateLocalStorage]
  );

  const toggleFavorite = useCallback(
    (path: string, name: string, icon?: string, category?: string): boolean => {
      const currentItems = getFavoriteItems();
      const existingItem = currentItems.find((item) => item.path === path);

      if (existingItem) {
        removeFavoriteItem(existingItem.id);
        return false; // Item supprimé
      } else {
        addFavoriteItem({
          name,
          path,
          icon,
          category,
        });
        return true; // Item ajouté
      }
    },
    [getFavoriteItems, addFavoriteItem, removeFavoriteItem]
  );

  const isFavorite = useCallback(
    (path: string): boolean => {
      return getFavoriteItems().some((item) => item.path === path);
    },
    [getFavoriteItems]
  );

  const moveFavoriteItem = useCallback(
    (itemId: string, direction: "up" | "down" | "top" | "bottom") => {
      const items = getFavoriteItems();
      const index = items.findIndex((item) => item.id === itemId);

      if (index === -1) return;

      let newIndex = index;
      if (direction === "up" && index > 0) newIndex = index - 1;
      else if (direction === "down" && index < items.length - 1) newIndex = index + 1;
      else if (direction === "top") newIndex = 0;
      else if (direction === "bottom") newIndex = items.length - 1;

      if (newIndex !== index) {
        const [movedItem] = items.splice(index, 1);
        items.splice(newIndex, 0, movedItem);

        // Réajuster l'ordre
        const reorderedItems = items.map((item, idx) => ({
          ...item,
          order: idx,
        }));

        dispatch({
          type: U2000FavoriteMenuActionType.REORDER_ITEMS,
          payload: { items: reorderedItems },
        });
        updateLocalStorage(reorderedItems);
      }
    },
    [getFavoriteItems, updateLocalStorage]
  );

  const reorderFavoriteItems = useCallback(
    (items: U2000FavoriteMenuItem[]) => {
      const reorderedItems = items.map((item, index) => ({
        ...item,
        order: index,
      }));

      dispatch({
        type: U2000FavoriteMenuActionType.REORDER_ITEMS,
        payload: { items: reorderedItems },
      });
      updateLocalStorage(reorderedItems);
    },
    [updateLocalStorage]
  );

  const getFilteredItems = useCallback(
    (filter?: U2000FavoriteMenuFilter) => {
      let items = getFavoriteItems();

      if (!filter) return items;

      if (filter.category) {
        items = items.filter((item) => item.category === filter.category);
      }

      if (filter.searchTerm) {
        const searchLower = filter.searchTerm.toLowerCase();
        items = items.filter(
          (item) => item.name.toLowerCase().includes(searchLower) || item.path.toLowerCase().includes(searchLower) || (item.description && item.description.toLowerCase().includes(searchLower))
        );
      }

      if (filter.showOnlyDefaults) {
        items = items.filter((item) => item.isDefault);
      }

      return items;
    },
    [getFavoriteItems]
  );

  const getItemsByCategory = useCallback(() => {
    const items = getFavoriteItems();
    return items.reduce((acc, item) => {
      const category = item.category || "Uncategorized";
      if (!acc[category]) acc[category] = [];
      acc[category].push(item);
      return acc;
    }, {} as Record<string, U2000FavoriteMenuItem[]>);
  }, [getFavoriteItems]);

  const getMostUsedItems = useCallback(
    (limit = 10) => {
      return getFavoriteItems()
        .filter((item) => item.useCount && item.useCount > 0)
        .sort((a, b) => (b.useCount || 0) - (a.useCount || 0))
        .slice(0, limit);
    },
    [getFavoriteItems]
  );

  const getRecentItems = useCallback(
    (limit = 10) => {
      return getFavoriteItems()
        .filter((item) => item.lastUsed)
        .sort((a, b) => new Date(b.lastUsed!).getTime() - new Date(a.lastUsed!).getTime())
        .slice(0, limit);
    },
    [getFavoriteItems]
  );

  const trackUsage = useCallback(
    (path: string) => {
      const item = getFavoriteItems().find((favItem) => favItem.path === path);
      if (item) {
        updateFavoriteItem(item.id, {
          lastUsed: new Date().toISOString(),
          useCount: (item.useCount || 0) + 1,
        });
      }
    },
    [getFavoriteItems, updateFavoriteItem]
  );

  const getSettings = useCallback(() => {
    return localSettings;
  }, [localSettings]);

  const updateSettings = useCallback(
    (settings: Partial<U2000FavoriteMenuSettings>) => {
      const newSettings = { ...localSettings, ...settings };
      setLocalSettings(newSettings);
    },
    [localSettings, setLocalSettings]
  );

  const getDefaultScreen = useCallback(() => {
    const settings = getSettings();
    const defaultPath = settings.defaultScreenPath;

    // Liste unifiée state + localStorage (BL-0337)
    const favoriteItems = getFavoriteItems();
    const decision = u2000ResolveDefaultScreenDecision(defaultPath, favoriteItems);

    // Si liste unifiée vide, retenter une lecture storage seule (défense multi-source)
    if (decision.path === null && defaultPath && favoriteItems.length === 0) {
      const fromStorage = readFavoriteItemsFromStorage();
      const storageDecision = u2000ResolveDefaultScreenDecision(defaultPath, fromStorage);
      if (storageDecision.path) {
        return storageDecision.path;
      }
      if (storageDecision.shouldClear) {
        updateSettings({ defaultScreenPath: undefined });
      }
      return null;
    }

    if (decision.shouldClear) {
      updateSettings({ defaultScreenPath: undefined });
    }
    return decision.path;
  }, [getSettings, getFavoriteItems, readFavoriteItemsFromStorage, updateSettings]);

  const setDefaultScreen = useCallback(
    (path: string) => {
      let favoriteItems = getFavoriteItems();
      if (!u2000CanSetDefaultScreenPath(path, favoriteItems)) {
        favoriteItems = readFavoriteItemsFromStorage();
      }
      if (u2000CanSetDefaultScreenPath(path, favoriteItems)) {
        updateSettings({ defaultScreenPath: path });
      }
    },
    [getFavoriteItems, readFavoriteItemsFromStorage, updateSettings]
  );

  const clearDefaultScreen = useCallback(() => {
    updateSettings({ defaultScreenPath: undefined });
  }, [updateSettings]);

  const exportFavorites = useCallback(() => {
    const data = {
      items: getFavoriteItems(),
      settings: getSettings(),
      exportDate: new Date().toISOString(),
      version: "1.0",
    };
    return JSON.stringify(data, null, 2);
  }, [getFavoriteItems, getSettings]);

  const importFavorites = useCallback(
    (jsonData: string): boolean => {
      try {
        const data = JSON.parse(jsonData);
        if (data.items && Array.isArray(data.items)) {
          const validItems = data.items.filter((item: any) => item.id && item.name && item.path);

          dispatch({
            type: U2000FavoriteMenuActionType.SET_ITEMS,
            payload: { items: validItems },
          });
          updateLocalStorage(validItems);

          if (data.settings) {
            updateSettings(data.settings);
          }

          return true;
        }
      } catch (error) {
        console.error("Failed to import favorites:", error);
      }
      return false;
    },
    [updateLocalStorage, updateSettings]
  );

  const cleanupOldItems = useCallback(
    (days = 30) => {
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - days);

      const items = getFavoriteItems();
      const cleanedItems = items.filter((item) => {
        if (!item.lastUsed || item.isDefault) return true;
        return new Date(item.lastUsed) > cutoffDate;
      });

      if (cleanedItems.length !== items.length) {
        dispatch({
          type: U2000FavoriteMenuActionType.SET_ITEMS,
          payload: { items: cleanedItems },
        });
        updateLocalStorage(cleanedItems);
      }
    },
    [getFavoriteItems, updateLocalStorage]
  );

  const resetFavorites = useCallback(() => {
    dispatch({
      type: U2000FavoriteMenuActionType.RESET_STATE,
      payload: {},
    });
    updateLocalStorage([]);
  }, [updateLocalStorage]);

  const reloadData = useCallback(() => {
    // Recharger les items depuis localStorage
    const itemsStorageKey = `${userSpecificKey}FavoriteMenus`;
    const storedItems = localStorage.getItem(itemsStorageKey);
    if (storedItems) {
      try {
        const items = JSON.parse(storedItems) as U2000FavoriteMenuItem[];
        dispatch({
          type: U2000FavoriteMenuActionType.SET_ITEMS,
          payload: { items },
        });
      } catch (error) {
        console.error("Error reloading items from localStorage:", error);
      }
    }

    // Recharger les settings depuis localStorage
    const settingsStorageKey = `${userSpecificKey}FavoriteMenuSettings`;
    const storedSettings = localStorage.getItem(settingsStorageKey);
    if (storedSettings) {
      try {
        const settings = JSON.parse(storedSettings) as U2000FavoriteMenuSettings;
        setLocalSettings(settings);
      } catch (error) {
        console.error("Error reloading settings from localStorage:", error);
      }
    }
  }, [userSpecificKey, setLocalSettings]);

  // Auto-cleanup si activé
  useEffect(() => {
    const settings = getSettings();
    if (settings.autoCleanupDays && settings.autoCleanupDays > 0) {
      cleanupOldItems(settings.autoCleanupDays);
    }
  }, [getSettings, cleanupOldItems]);

  return {
    // Gestion des items
    getFavoriteItems,
    addFavoriteItem,
    removeFavoriteItem,
    updateFavoriteItem,
    toggleFavorite,
    isFavorite,

    // Gestion de l'ordre
    moveFavoriteItem,
    reorderFavoriteItems,

    // Gestion des filtres et recherche
    getFilteredItems,
    getItemsByCategory,
    getMostUsedItems,
    getRecentItems,

    // Gestion de l'usage
    trackUsage,

    // Gestion des settings
    getSettings,
    updateSettings,

    // Gestion de l'écran par défaut
    getDefaultScreen,
    setDefaultScreen,
    clearDefaultScreen,

    // Utilitaires
    exportFavorites,
    importFavorites,
    cleanupOldItems,
    resetFavorites,
    reloadData,

    // État
    isLoading,
    itemCount: state.items.length,
  };
}
